/**
 * Utility functions for CSV export
 */

export interface CSVColumn {
  key: string;
  label: string;
  formatter?: (value: any) => string;
}

/**
 * Converts an array of objects to CSV format
 */
export const convertToCSV = (data: any[], columns: CSVColumn[]): string => {
  if (!data || data.length === 0) {
    return '';
  }

  // Create header row
  const headers = columns.map(col => `"${col.label}"`).join(',');
  
  // Create data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.key];
      
      // Apply formatter if provided
      if (col.formatter && value !== null && value !== undefined) {
        value = col.formatter(value);
      }
      
      // Handle null/undefined values
      if (value === null || value === undefined) {
        value = '';
      }
      
      // Convert to string and escape quotes
      const stringValue = String(value).replace(/"/g, '""');
      
      return `"${stringValue}"`;
    }).join(',');
  });

  return [headers, ...rows].join('\n');
};

/**
 * Downloads CSV content as a file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Formats a date for CSV export
 */
export const formatDateForCSV = (date: string | Date): string => {
  try {
    const d = new Date(date);
    return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
  } catch {
    return String(date);
  }
};

/**
 * Formats an object for CSV export
 */
export const formatObjectForCSV = (obj: any): string => {
  if (obj === null || obj === undefined) return '';
  if (typeof obj === 'object') {
    return JSON.stringify(obj);
  }
  return String(obj);
};
